/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import React, { RefObject } from 'react';

import { ICheckoutSectionContainerItem, ICheckoutSectionContainerViewProps } from '@msdyn365-commerce-modules/checkout/src/modules/checkout-section-container/./checkout-section-container';
import { ICheckoutSectionContainerProps } from '../definition-extensions/checkout-section-container.ext.props.autogenerated';
import { PaymentContext } from './checkout.view';

const ItemComponent: React.FC<ICheckoutSectionContainerItem> = ({ loading, itemProps, item }) => {
	return (
		<>
			{loading}
			<Node {...itemProps}>{item}</Node>
		</>
	);
};

const useMutationObserver = (
	ref: RefObject<any>,
	callback: MutationCallback,
	options = {
		attributes: true,
		attributeFilter: ['class'],
		characterData: true,
		childList: true,
		subtree: true,
	}
) => {
	React.useEffect(() => {
		if (ref.current !== null) {
			const observer = new MutationObserver(callback);
			observer.observe(ref.current, options);

			return () => observer.disconnect();
		} else
			return;

	}, [callback, options]);
};

const CheckoutSectionContainerView: React.FC<ICheckoutSectionContainerViewProps & ICheckoutSectionContainerProps<ICheckoutSectionContainerViewProps>> = props => {
	const { paymentId, setPaymentId, setPlaceOrderButtonVisible } = React.useContext(PaymentContext);

	const mutationObserver = React.useRef<HTMLDivElement>(null);
	const [accountPayment, setAccountPayment] = React.useState(false);
	const accountPaymentStatus = (ref: any) => {
		ref.forEach((item: any) => {
			if((item.target.classList.contains('hidden')) && (item.target.dataset.typeName = 'nos-customer-account-payment')) {
				setAccountPayment(true);
			} else if(accountPayment === true) {
				setAccountPayment(false);
			}
		});
	};
	useMutationObserver(mutationObserver, accountPaymentStatus);

	const {
		checkoutPlainContainerProps,
		items,
		resources
	} = props;

	return (
		<Module {...checkoutPlainContainerProps}>
			<div ref={mutationObserver}>
				{items.map(item => {

					// Check if module type equals default Payment module
					let isCreditCard = Object.values(item.itemProps).includes('checkout-payment-instrument');
					// Check if module type equals Sepa Payment module
					let isSepa = Object.values(item.itemProps).includes('nos-payment-instrument');
					let isPayment = isCreditCard || isSepa;
					let currentPaymentModule = item.id;

					return (
						<>
							{isCreditCard && !accountPayment &&
								<div className='ms-form'>
									<input className='ms-form__input-radio' type="radio" defaultChecked name="paymentModule" id={item.id} value={item.id} onChange={(event: any) => { setPaymentId(event.target.value), setPlaceOrderButtonVisible(true) }} />
									<label className='ms-form__label-radio' htmlFor={item.id}>{resources.paymentMethodDefaultPayment}</label>
								</div>
							}
							{isSepa && !accountPayment &&
								<div className='ms-form'>
									<input className='ms-form__input-radio' type="radio" name="paymentModule" id={item.id} value={item.id} onChange={(event: any) => { setPaymentId(event.target.value); setPlaceOrderButtonVisible(false) }} />
									<label className='ms-form__label-radio' htmlFor={item.id}>{resources.paymentMethodSepa}</label>
								</div>
							}

							{/* Render all non Payment modules */}
							{!isPayment &&
								<ItemComponent key={item.id} {...item} />
							}

							{/* Render Payment module but only if user has selected it by choosing the corresponding radion button */}
							{isPayment && currentPaymentModule === paymentId &&
								<ItemComponent key={item.id} {...item} />
							}
							{isCreditCard && paymentId === '' &&
								<ItemComponent key={item.id} {...item} />
							}
						</>
					);
				})}
			</div>
		</Module>
	);
};

export default CheckoutSectionContainerView;
